import type { BatchValue } from './context';
import { applyCaps } from './caps';
import type { BatchFileInput } from './types';

/**
 * Batch file intake, shared by the standalone Batch route and the Converter's
 * single-vs-many drop zone (cue-forge-9px). Extracted so both entry points apply
 * the same caps + byte-read + queue-reset.
 */

/** Read a File's bytes, preferring Blob.arrayBuffer with a FileReader fallback (jsdom). */
export async function readBytes(file: File): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === 'function') {
    return new Uint8Array(await file.arrayBuffer());
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error ?? new Error('File read failed'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Apply the intake caps and read the accepted files' bytes. Everything the
 * shared Convert drop zone advertises is queued — including Pro formats
 * (.scc/.stl/.ttml/.itt/.dfxp/.xml), which the batch engine now parses via the
 * same lazy Pro codecs as the single-file path (cue-forge-drt.16; parsing is
 * license-gated per run through `BatchJob.allowPro`). Unrecognized files are
 * queued too: the engine reports a clear per-file "unrecognized format" error
 * result rather than silently dropping them here.
 */
export async function ingestFiles(
  picked: File[],
): Promise<{ inputs: BatchFileInput[]; notices: string[] }> {
  const { accepted, notices } = applyCaps(picked);
  const inputs: BatchFileInput[] = await Promise.all(
    accepted.map(async (f) => ({ name: f.name, bytes: await readBytes(f) })),
  );
  return { inputs, notices };
}

/** Put already-ingested inputs into the queue, clearing any prior run's results. */
export function applyInputs(batch: BatchValue, inputs: BatchFileInput[], notices: string[]): void {
  batch.setFiles(inputs);
  batch.setNotices(notices);
  batch.setResults([]);
  batch.setRunJob(null);
  batch.setRunError(null);
  batch.setProgress(null);
}

/** Ingest picked files into the batch queue, clearing any prior run's results. */
export async function applyIngest(batch: BatchValue, picked: File[]): Promise<void> {
  const { inputs, notices } = await ingestFiles(picked);
  applyInputs(batch, inputs, notices);
}

/** Empty the batch queue and any prior run's results/notices. */
export function clearBatch(batch: BatchValue): void {
  batch.setFiles([]);
  batch.setNotices([]);
  batch.setResults([]);
  batch.setRunJob(null);
  batch.setRunError(null);
  batch.setProgress(null);
}
